import {T} from '../../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { FETCH_CONFIG } from '../../../components/Product/ProductSidebarClub/ProductSidebarClub';
import { GET_BACKEND_CONFIG } from '../../../commonQueries';
import {
  UPDATE_CUSTOMER
} from '../../PersonalAreaScreen/queriesPersonalArea';
import { GET_CARD } from '../../../queries/customer';
import { useCartContext } from '../../../context/CartContext';
import { sendTransactionQuarticon } from '../../../hooks/useQuarticonHooks';
import { usePopup } from '../../../context/PopupContext';
import { useUserContext } from '../../../context/UserContext';
import { useModal } from '../../../hooks/useModal';
import { purchaseEvent } from '../../../hooks/useAnalytics';
import usePlaceOrder from '../../../hooks/usePlaceOrder';
import useSetOrderNote from '../../../hooks/useSetOrderNote';
import { useQuarticonContext } from '../../../context/QuarticonContext';
import calculateReferenceNumber from '../../../utils/calculateReferenceNumber';
import KeksPay from '../PaymentMethods/KeksPay';
import appConfiguration from '../../../config/default.json';
import readConfig from '../../../utils/readClientConfiguration';
import { useNewMonriPayment } from '../../../hooks/useNewMonriPayment';
import { useReservedOrderId } from '../../../hooks/useReserveOrderId';
import resolveOrderNumberRs from "../../../utils/resolveOrderNumberRs";

const referenceModel = readConfig(appConfiguration, 'referenceModel');
const PAYMENT_METHOD_CODE_MONRI = 'pikpay';
const PAYMENT_METHOD_CODE_KEKS_PAY = 'kekspay';
const PAYMENT_METHOD_CODE_BANK_TRANSFER = 'banktransfer';
const monriCheckoutType = readConfig(appConfiguration, 'monriCheckoutType');
const monriCheckoutLanguage = readConfig(appConfiguration, 'monriCheckoutLanguage');
const monriBaseUrl = readConfig(appConfiguration, 'monriBaseUrl');
const monriAuthenticityToken = readConfig(appConfiguration, 'monriAuthenticityToken');
const currencyCode = readConfig(appConfiguration, 'currencyCode');

const monriFormReference = React.createRef();
const monriDigestReference = React.createRef();
const monriOrderInfoReference = React.createRef();

const CheckoutPlaceOrderButton = ({ isChecked, isOrderCreating, subscriptionChecked }) => {
  const [placingOrder, setPlacingOrder] = useState(false);
  const { placeOrder } = usePlaceOrder();
  const [generatePaymentData, generatePaymentDigestData] = useNewMonriPayment();
  const { tracker } = useQuarticonContext();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { data: card } = useQuery(GET_CARD, {
    fetchPolicy: 'no-cache',
    skip: !user,
  });

  const { success } = (card && card.historyCustomerLoyaltyCard) || {};
  const { t } = useTranslation();
  const { showAddToCartPopupMessage } = usePopup();
  const reserveOrderId = useReservedOrderId();

  const { cart, setOrderNote: clearOrderNote, orderNote } = useCartContext();
  const { setOrderNote } = useSetOrderNote();
  const grandTotal = (cart && cart.prices.grand_total.value) || 0;
  const { data: backendConfig } = useQuery(GET_BACKEND_CONFIG, {
    fetchPolicy: 'cache-first',
  });
  const { data: config } = useQuery(FETCH_CONFIG);
  const rate = config && config.backendConfig.shop.loyaltyConversion;
  const loyaltyPoints = rate && (Math.floor(grandTotal / rate) || 0);
  const loyaltyPointsBanking = loyaltyPoints && loyaltyPoints * 2;
  const { isOpen, openModal, closeModal } = useModal();

  // Up to 30 characters are allowed by Monri
  const customerFullName = `${cart.billing_address.firstname} ${cart.billing_address.lastname}`.substring(0, 30);

  const monriCheckoutRedirectComponent = (
    <form style={{ display: 'none' }} method="POST" action={`${monriBaseUrl}v2/form`} ref={monriFormReference}>
      <input type="hidden" name="ch_full_name" value={customerFullName} />
      <input type="hidden" name="ch_address" value={cart.billing_address.street.join(' ')} />
      <input type="hidden" name="ch_city" value={cart.billing_address.city} />
      <input type="hidden" name="ch_zip" value={cart.billing_address.postcode} />
      <input type="hidden" name="ch_country" value={cart.billing_address.country.code.toUpperCase()} />
      <input type="hidden" name="ch_phone" value={cart.billing_address.telephone} />
      <input type="hidden" name="ch_email" value={cart.email} />
      <input type="hidden" name="order_info" ref={monriOrderInfoReference} />
      <input type="hidden" name="order_number" value={cart.cart_id} />
      <input type="hidden" name="amount" value={Math.round(parseFloat(cart.prices.grand_total.value) * 100)} />
      <input type="hidden" name="currency" value={currencyCode} />
      <input type="hidden" name="language" value={monriCheckoutLanguage} />
      <input type="hidden" name="transaction_type" value="authorize" />
      <input type="hidden" name="authenticity_token" value={monriAuthenticityToken} />
      <input type="hidden" name="digest" ref={monriDigestReference} />
      <button type="submit" />
    </form>
  );

  const onOrderHandler = async () => {
    if (!isChecked) {
      return;
    }
    try {
      setPlacingOrder(true);

      if (orderNote) {
        await setOrderNote({
          note: orderNote,
          email: cart.email,
          method: cart.selected_payment_method.code,
        });
      }

      if (cart.selected_payment_method.code === PAYMENT_METHOD_CODE_MONRI && monriCheckoutType === 'redirect') {
        try {
          const digestResponse = await generatePaymentDigestData(
            Math.round(parseFloat(cart.prices.grand_total.value) * 100),
            cart.cart_id.toString(),
          );
          // TODO: debug why digest is not valid
          console.log('response', digestResponse);

          monriOrderInfoReference.current.value = await reserveOrderId();
          monriDigestReference.current.value = digestResponse;

          monriFormReference.current.submit();

          // TODO: redirect back & place order
        } catch (error) {
          console.error(error, error.code, error.message);
          showAddToCartPopupMessage(t('cartSummary.errorWithMonriPayment'), 'error', 4000);
        } finally {
          setPlacingOrder(false);
        }

        return false;
      }

      const { data: orderData } = await placeOrder();
      clearOrderNote('');
      if (orderData.placeOrder && orderData.placeOrder.order.order_number) {
        // eslint-disable-next-line no-restricted-globals
        navigate('/checkout/success', {
          state: {
            orderNumber: orderData.placeOrder.order.frontend_id,
            reference: {
              number:
                  cart.selected_payment_method.code === PAYMENT_METHOD_CODE_BANK_TRANSFER
                      ? calculateReferenceNumber(orderData.placeOrder.order.frontend_id, '')
                      : null,
              model: referenceModel,
              rsNumber: resolveOrderNumberRs(calculateReferenceNumber(orderData.placeOrder.order.frontend_id, ''), cart, referenceModel)
            },
            paymentMethod: cart.selected_payment_method.code,
            loyaltyPoints:
                cart.selected_payment_method.code === PAYMENT_METHOD_CODE_MONRI ? loyaltyPoints : loyaltyPointsBanking,
            email: cart.email,
            isLoyaltyMember: success,
            isLoggedIn: user,
            cart: cart,
            currencyCode: currencyCode
          }
        });

        const orderId = orderData.placeOrder.order.order_number;
        const copyCart = JSON.parse(JSON.stringify(cart));
        copyCart.reserved_order_id = orderId;

        purchaseEvent(copyCart, orderId, backendConfig);
        sendTransactionQuarticon(copyCart, tracker);
      }
    } catch (err) {
      if (err.message.includes('maximum qty')) {
        const productRegex = /for product (.*?) is/i;
        const product = productRegex.exec(err.message);
        if (product && product[1]) {
          const message = t('messagePopup.orderQtyError').replace('[product]', product[1]);
          showAddToCartPopupMessage(message, 'error', 4000);
        } else {
          showAddToCartPopupMessage(err.message, 'error', 4000);
        }
      } else {
        showAddToCartPopupMessage(err.message, 'error', 4000);
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  const shippingAddressSet = cart && cart.shipping_addresses && cart.shipping_addresses.length > 0;
  const shippingMethodSet = shippingAddressSet && cart.shipping_addresses[0].selected_shipping_method;
  const billingAddressSet = cart && cart.billing_address;
  const paymentMethodSet = cart && cart.selected_payment_method && cart.selected_payment_method.code;
  const cartEmailSet = cart && cart.email;
  const enabled =
    shippingAddressSet && shippingMethodSet && billingAddressSet && paymentMethodSet && cartEmailSet && isChecked;

  return (
    <div className="place-order-button">
      {isOpen && <KeksPay closeModal={closeModal} />}
      {cart.selected_payment_method.code === PAYMENT_METHOD_CODE_MONRI && monriCheckoutRedirectComponent}
      {/* eslint-disable-next-line react/button-has-type */}
      <button
        className="checkout__btn checkout__btn--secondary"
        title={t('cartSummary.finishYourOrder')}
        disabled={!enabled || placingOrder || isOrderCreating}
        onClick={() => {
          if (cart.selected_payment_method.code === PAYMENT_METHOD_CODE_KEKS_PAY) {
            return openModal();
          }

          // if (cart.selected_payment_method.code === PAYMENT_METHOD_CODE_MONRI && monriCheckoutType === 'components') {
          //   return false;
          // }

          return onOrderHandler();
        }}
      >
        <T id="cartSummary.finishYourOrder" />
      </button>
    </div>
  );
};

export default CheckoutPlaceOrderButton;
